import { useStyletron } from '@thanx/uikit/theme'
import PageHeader from 'components/PageHeader'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabPane from 'components/Tabs/TabPane'
import { useHashTab } from 'components/Tabs/useHashTab'
import {
  LocationContext,
  LocationContextProvider,
} from 'contexts/LocationContext'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import { buildTranslate } from 'locales'
import React, { useContext, useEffect } from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { selectCumulativeMetric } from 'selectors/captureRateMetric'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import { history } from 'store'
import { useAbility } from 'utilities/ability'
import Stub from '../components/Stub'
import AveragePurchasesPerLocationPerMonthTab from './AveragePurchasesPerLocationPerMonthTab'
import Content from './Content'
import NoContent from './NoContent'
import PurchasesAtSelectLocationsTab from './PurchasesAtSelectLocationsTab'
import RevenueSources from './RevenueSources'
import { UploadStateProvider } from './UploadStateContext'
import { crrAccessState } from './utilities'

const t = buildTranslate('capture_rate')

// This page had no tab structure before (a single bespoke
// `Content`/`NoContent` view gated on the trial-eligibility rule
// `crrAccessState`, unrelated to RBAC). The Revenue sources tab is gated on
// `mx-capture-rate-pos-parity` + the 'LocationReports' permission; the 2
// location-scoped tabs below are gated on `mx-promote-insights-lab-reports`
// + the same permission. The two extra-tab groups are additive and
// independently flagged, so any combination of the two flags can be on.
enum Tabs {
  CAPTURE_RATE = 'capture_rate',
  REVENUE_SOURCES = 'revenue_sources',
  PURCHASES_AT_SELECT_LOCATIONS = 'purchases_at_select_locations',
  AVERAGE_PURCHASES_PER_LOCATION_PER_MONTH = 'average_purchases_per_location_per_month',
}

const LOCATION_SCOPED_TABS: Tabs[] = [
  Tabs.PURCHASES_AT_SELECT_LOCATIONS,
  Tabs.AVERAGE_PURCHASES_PER_LOCATION_PER_MONTH,
]

// Exact match via the shared helper, not `hash.includes(...)`. A substring
// scan resolved any hash merely mentioning a tab id to that tab — e.g.
// #invalid_purchases_at_select_locations selected Purchases at select
// location(s). Permission/flag gating stays in the effect below, which also
// owns rewriting a restricted hash; this only decides what the hash names.
const ALL_TABS = Object.values(Tabs)

const CaptureRateReport: React.FC = () => {
  const merchant = useCurrentMerchant()
  const [css] = useStyletron()
  const { currentLocation } = useContext(LocationContext)
  const cumulativeMetrics = useSelector(state =>
    selectCumulativeMetric(state, merchant?.id, currentLocation?.id)
  )
  const revenueSource = cumulativeMetrics?.revenue_source || 'manual'

  // TODO: remove this flag for public release
  const usePosData = useFlag('use-pos-data-for-crr', false)

  // Gates the 2 location-scoped tabs added by the "Promote Insights Lab
  // reports" initiative. Shared across every PR in that initiative so they
  // can all be flipped on together.
  const promoteInsightsLabReports = useFlag(
    'mx-promote-insights-lab-reports',
    false
  )
  // Gates the Revenue sources tab. Owner: Loyalty squad
  // remove 1-3 months after 100% rollout with no incidents.
  const captureRatePosParity = useFlag('mx-capture-rate-pos-parity', false)
  const ability = useAbility()
  const canAccessRevenueSources =
    captureRatePosParity && ability.can('access', 'LocationReports')
  const canAccessLocationReports =
    promoteInsightsLabReports && ability.can('access', 'LocationReports')
  // True if the ability+flag check passes for either extra tab group.
  const hasExtraTabs = canAccessRevenueSources || canAccessLocationReports
  // Flag-only (ignores ability), unlike `hasExtraTabs`: must stay true so
  // the redirect effect below still clears a restricted hash even when the
  // ability denies access.
  const anyExtraTabFlagOn = promoteInsightsLabReports || captureRatePosParity

  const [activeTab, setActiveTab] = useHashTab(ALL_TABS, Tabs.CAPTURE_RATE)

  const areFlagsReady = useFlagsReady()
  const merchantUser = useSelector(selectCurrentMerchantUser)

  useEffect(() => {
    // With both flags off this page renders no tabs at all (master's
    // shape), so there is nothing to fall back to and nothing here ever
    // touched the hash — leave the URL exactly as we found it.
    if (!anyExtraTabFlagOn) return

    // Wait for everything access is derived from. The flag checks above only
    // cover the flag half; the other half is
    // `ability.can('access', 'LocationReports')`, and the ability is built
    // from merchant + merchantUser — `defineAbilitiesFor(null, null)` grants
    // only `manage Login`, so it reads false while those are still loading
    // exactly as it does when access is genuinely denied. This effect's
    // action is irreversible: it rewrites the URL hash and nothing here ever
    // restores it, so firing from an unresolved state doesn't briefly
    // mis-render a tab, it destroys the deep link.
    if (!areFlagsReady || !merchant || !merchantUser) return

    const activeTabIsInaccessible =
      (activeTab === Tabs.REVENUE_SOURCES && !canAccessRevenueSources) ||
      (LOCATION_SCOPED_TABS.includes(activeTab) && !canAccessLocationReports)

    if (activeTabIsInaccessible) {
      // Mirror handleSwitchTab: keep the URL hash in sync with the tab we
      // fall back to, so a bookmarked/shared restricted hash doesn't linger
      // in the address bar while a different tab is rendered. Spread the
      // current location so a query string survives — `history.replace({
      // hash })` alone resets `search` to '' (history 4.x createLocation).
      history.replace({ ...history.location, hash: Tabs.CAPTURE_RATE })
      setActiveTab(Tabs.CAPTURE_RATE)
    }
  }, [
    areFlagsReady,
    merchant,
    merchantUser,
    anyExtraTabFlagOn,
    canAccessRevenueSources,
    canAccessLocationReports,
    activeTab,
    setActiveTab,
  ])

  const handleSwitchTab = (tab: Tabs) => {
    // Spread the current location so a query string isn't dropped; see the
    // effect above.
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  if (!merchant) {
    return <Stub type="captureRate" />
  }

  const reportAccessState = crrAccessState(merchant)
  const remainingDays =
    reportAccessState === 'available' || reportAccessState === 'coming_soon'
      ? null
      : reportAccessState

  const captureRateContent =
    reportAccessState === 'available' ? (
      <Content
        merchant={merchant}
        hasIntegrations={usePosData && revenueSource === 'pos'}
      />
    ) : (
      <NoContent remainingDays={remainingDays} />
    )

  return (
    <UploadStateProvider>
      <PageHeader title={t('title')} description={t('subtitle')}>
        <Helmet>
          <title>
            {hasExtraTabs ? t(`tabs.${activeTab}.page_title`) : t('page_title')}
          </title>
        </Helmet>
        {hasExtraTabs ? (
          <Tab.Container
            activeKey={activeTab}
            transition={false}
            onSelect={(tab: string | null) => {
              handleSwitchTab(tab as Tabs)
            }}
          >
            {/* ScrollingTabNav rather than TabNav: up to 4 labels are long
                enough to wrap onto a second row at common widths, which stops
                reading as a tab bar.
                markup and is deliberately left alone. */}
            <ScrollingTabNav ariaLabel={t('title')}>
              <TabItem
                state={Tabs.CAPTURE_RATE}
                title={t('tabs.capture_rate.title')}
              />
              {canAccessLocationReports && (
                <TabItem
                  state={Tabs.PURCHASES_AT_SELECT_LOCATIONS}
                  title={t('tabs.purchases_at_select_locations.title')}
                />
              )}
              {canAccessLocationReports && (
                <TabItem
                  state={Tabs.AVERAGE_PURCHASES_PER_LOCATION_PER_MONTH}
                  title={t(
                    'tabs.average_purchases_per_location_per_month.title'
                  )}
                />
              )}
              {canAccessRevenueSources && (
                <TabItem
                  state={Tabs.REVENUE_SOURCES}
                  title={t('tabs.revenue_sources.title')}
                />
              )}
            </ScrollingTabNav>
            <Tab.Content
              className={`pb-xxl h-100 grey-05-bkg ${css({
                minHeight: 'calc(100% - 75px)',
              })}`}
            >
              <TabPane state={Tabs.CAPTURE_RATE}>{captureRateContent}</TabPane>
              {canAccessLocationReports && (
                <TabPane
                  state={Tabs.PURCHASES_AT_SELECT_LOCATIONS}
                  mountOnEnter
                >
                  <PurchasesAtSelectLocationsTab />
                </TabPane>
              )}
              {canAccessLocationReports && (
                <TabPane
                  state={Tabs.AVERAGE_PURCHASES_PER_LOCATION_PER_MONTH}
                  mountOnEnter
                >
                  <AveragePurchasesPerLocationPerMonthTab />
                </TabPane>
              )}
              {canAccessRevenueSources && (
                <TabPane state={Tabs.REVENUE_SOURCES} mountOnEnter>
                  <LocationContextProvider>
                    <RevenueSources />
                  </LocationContextProvider>
                </TabPane>
              )}
            </Tab.Content>
          </Tab.Container>
        ) : (
          <div
            className={`grey-05-bkg border-top-1 pb-xxl grey-20-border pt-xl ${css(
              {
                minHeight: 'calc(100% - 75px)',
              }
            )}`}
          >
            <Container>{captureRateContent}</Container>
          </div>
        )}
      </PageHeader>
    </UploadStateProvider>
  )
}

export default CaptureRateReport
